import { Link } from "@react-navigation/native";
import { NIL } from "uuid";
import {
    useThemedStyle,
    useModal,
    useToast,
    Alert,
    Button,
    TextInputModal,
    Loading,
    Surface,
    Headline,
    Spacer,
    Text,
    StyleFunction,
} from "@venuepos/react-common";
import {
    useIntegrationsOfTypeQuery,
    useIntegrationSettingsLazyQuery,
    useFiskalyClientSyncMutation,
    useFiskalyClientDeregisterMutation,
    useFiskalyClientRegisterMutation,
    useFiskalyClientByCashRegisterIdLazyQuery,
} from "graphql-sdk";
import { ICashRegister, FiskalySettings, formatDate } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import { useCallback, useEffect } from "react";
import React, { View } from "react-native";
import { useHandleMutationError } from "../../../hooks";
import { useAdminSession } from "../../../session";

export function FiskalyClient({
    cashRegisterId,
}: {
    cashRegisterId: ICashRegister["id"];
}) {
    const [{ locale }] = useAdminSession(["locale"]);
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const fiskalyIntegrations = useIntegrationsOfTypeQuery({
        variables: {
            type: "FISKALY",
        },
    });

    const fiskalyIntegration =
        fiskalyIntegrations?.data?.integrationsOfType?.data[0];

    const [getIntegrationSettings, integrationSettings] =
        useIntegrationSettingsLazyQuery();

    const [getFiskalyClient, fiskalyClient] =
        useFiskalyClientByCashRegisterIdLazyQuery({
            fetchPolicy: "no-cache",
        });

    const [registerWithFiskaly] = useFiskalyClientSyncMutation();
    const [deactivateCashRegister] = useFiskalyClientDeregisterMutation();
    const [activateCashRegister] = useFiskalyClientRegisterMutation();
    const { handleMutationError } = useHandleMutationError();
    const { render } = useModal();
    const toast = useToast();

    const getPin = useCallback(async () => {
        return await render(onClose => (
            <TextInputModal
                onClose={onClose}
                title={t(
                    "backoffice.integration.fiskaly.tss_admin_title",
                    "Fiskaly: Authentication"
                )}
                label={t(
                    "backoffice.integration.fiskaly.tss_admin_pin",
                    "Enter the Admin PIN for TSS operations"
                )}
            />
        ));
    }, [render, t]);

    const save = useCallback(async () => {
        if (!fiskalyIntegration) {
            return;
        }

        const pinUserInput = await getPin();

        if (!pinUserInput) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.no_pin",
                    "No PIN was entered."
                )
            );
            return;
        }

        await handleMutationError(
            async () => {
                await registerWithFiskaly({
                    variables: {
                        id: cashRegisterId,
                        pin: pinUserInput,
                    },
                });
            },
            t(
                "backoffice.integration.fiskaly.cash_register_synced_success",
                "Cash register registered with Fiskaly. Ready for use."
            ),
            async () => {
                fiskalyClient.refetch();
            }
        );
    }, [
        cashRegisterId,
        fiskalyClient,
        fiskalyIntegration,
        getPin,
        handleMutationError,
        registerWithFiskaly,
        t,
        toast,
    ]);

    const deregister = useCallback(async () => {
        if (!fiskalyIntegration) {
            return;
        }

        if (fiskalyClient.loading) {
            return;
        }

        if (!fiskalyClient.data.fiskalyClientByCashRegisterId) {
            return;
        }

        const pinUserInput = await getPin();

        if (!pinUserInput) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.no_pin",
                    "No PIN was entered."
                )
            );
            return;
        }

        await handleMutationError(
            async () => {
                await deactivateCashRegister({
                    variables: {
                        id: fiskalyClient.data.fiskalyClientByCashRegisterId.id,
                        integrationId: fiskalyIntegration.id,
                        pin: pinUserInput,
                    },
                });
            },
            t(
                "backoffice.integration.fiskaly.cash_register_deactivated",
                "Cash register deactivated with Fiskaly."
            ),
            async () => {
                fiskalyClient.refetch();
            }
        );
    }, [
        deactivateCashRegister,
        fiskalyClient,
        fiskalyIntegration,
        getPin,
        handleMutationError,
        t,
        toast,
    ]);

    const activate = useCallback(async () => {
        if (!fiskalyIntegration) {
            return;
        }

        if (fiskalyClient.loading) {
            return;
        }

        if (!fiskalyClient.data.fiskalyClientByCashRegisterId) {
            return;
        }

        const pinUserInput = await getPin();

        if (!pinUserInput) {
            toast.warning(
                t(
                    "backoffice.integration.fiskaly.no_pin",
                    "No PIN was entered."
                )
            );
            return;
        }

        await handleMutationError(
            async () => {
                await activateCashRegister({
                    variables: {
                        id: fiskalyClient.data.fiskalyClientByCashRegisterId.id,
                        integrationId: fiskalyIntegration.id,
                        pin: pinUserInput,
                    },
                });
            },
            t(
                "backoffice.integration.fiskaly.cash_register_activated",
                "Cash register activated with Fiskaly."
            ),
            async () => {
                fiskalyClient.refetch();
            }
        );
    }, [
        activateCashRegister,
        fiskalyClient,
        fiskalyIntegration,
        getPin,
        handleMutationError,
        t,
        toast,
    ]);

    useEffect(() => {
        if (!fiskalyIntegration) {
            return;
        }

        getFiskalyClient({
            variables: { cashRegisterId },
        });
    }, [cashRegisterId, fiskalyIntegration, getFiskalyClient]);

    useEffect(() => {
        if (!fiskalyIntegration) {
            return;
        }

        getIntegrationSettings({
            variables: { integrationId: fiskalyIntegration.id },
        });
    }, [fiskalyIntegration, getIntegrationSettings]);

    const fiskalyClientData =
        fiskalyClient?.data?.fiskalyClientByCashRegisterId;
    const integrationSettingsData = integrationSettings?.data
        ?.integrationSettings as FiskalySettings;

    const hasOrganization =
        integrationSettingsData &&
        integrationSettingsData?.organizationId !== NIL;

    const hasTss =
        integrationSettingsData && integrationSettingsData?.tssId !== NIL;

    const isSubmitted = integrationSettingsData?.submissionNeeded;

    const hasEstablishment =
        fiskalyClientData &&
        fiskalyClientData.establishmentId &&
        fiskalyClientData.establishmentId !== NIL;

    if (!fiskalyIntegration) {
        return;
    }

    if (fiskalyIntegrations.loading) {
        return <Loading />;
    }

    return (
        <Surface style={styles.asideContent}>
            <Headline size="h5">
                {t(
                    "backoffice.integration.fiskaly.client_title",
                    "Fiskaly Client"
                )}
            </Headline>
            {!integrationSettingsData ? (
                <Alert type="warning" swapColors>
                    {t(
                        "backoffice.integration.fiskaly.no_settings_found",
                        "No integration settings found."
                    )}
                </Alert>
            ) : null}

            {fiskalyClientData ? (
                <>
                    {!hasEstablishment ? (
                        <Alert type="info" swapColors>
                            <>
                                <View>
                                    <Text>
                                        {t(
                                            "backoffice.integration.fiskaly.client_not_registered",
                                            "This cash register is not registered as a client with an establishment."
                                        )}
                                    </Text>
                                </View>

                                <View>
                                    <Link
                                        style={styles.link}
                                        to={{
                                            screen: "INTEGRATION_EDIT",
                                            params: {
                                                id: fiskalyIntegration.id,
                                            },
                                        }}
                                    >
                                        {t(
                                            "backoffice.integration.fiskaly.go_to_settings",
                                            "Go to the Integration Settings and fix this."
                                        )}
                                    </Link>
                                </View>
                            </>
                        </Alert>
                    ) : (
                        <Text>
                            {t(
                                "backoffice.integration.fiskaly.client_registered",
                                "Cash register is registered as a client with an establishment."
                            )}
                        </Text>
                    )}
                    {!isSubmitted && fiskalyClientData.submitBefore ? (
                        <Alert type="info" swapColors>
                            <>
                                <View>
                                    <Text>
                                        {t(
                                            "backoffice.integration.fiskaly.client_not_submitted",
                                            "This cash register is not submitted to the German tax authorities yet."
                                        )}
                                    </Text>
                                </View>

                                <View>
                                    <Link
                                        style={styles.link}
                                        to={{
                                            screen: "INTEGRATION_EDIT",
                                            params: {
                                                id: fiskalyIntegration.id,
                                            },
                                        }}
                                    >
                                        {t(
                                            "backoffice.integration.fiskaly.must_submit_before",
                                            "Must be submitted before {{date}}.",
                                            {
                                                date: formatDate(
                                                    fiskalyClientData?.submitBefore,
                                                    locale as AvailableLocale
                                                ),
                                                interpolation: {
                                                    escapeValue: false,
                                                },
                                            }
                                        )}
                                    </Link>
                                </View>
                            </>
                        </Alert>
                    ) : null}
                    <Spacer />
                    {fiskalyClientData.clientState === "REGISTERED" ? (
                        <>
                            <View>
                                <Text>
                                    {t(
                                        "backoffice.integration.fiskaly.client_state_registered",
                                        "This cash register is active with a TSS. Invoices and transactions will be signed."
                                    )}
                                </Text>
                            </View>
                            <Button
                                onPress={async () => {
                                    await deregister();
                                }}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.deactivate_client",
                                    "Deactivate client"
                                )}
                            </Button>
                        </>
                    ) : (
                        <>
                            <View>
                                <Text>
                                    {t(
                                        "backoffice.integration.fiskaly.client_state_deregistered",
                                        "This cash register is not active with a TSS. Invoices and transactions will not be signed."
                                    )}
                                </Text>
                            </View>
                            <Button
                                onPress={async () => {
                                    await activate();
                                }}
                            >
                                {t(
                                    "backoffice.integration.fiskaly.activate_client",
                                    "Activate client"
                                )}
                            </Button>
                        </>
                    )}
                </>
            ) : !hasOrganization ? (
                <Alert type="warning">
                    {t(
                        "backoffice.integration.fiskaly.no_organization_found",
                        "The Fiskaly Integration does not have an organization registered."
                    )}
                    <Link
                        style={styles.alertLink}
                        to={{
                            screen: "INTEGRATION_EDIT",
                            params: { id: fiskalyIntegration.id },
                        }}
                    >
                        {t(
                            "backoffice.integration.fiskaly.go_to_settings",
                            "Go to the Integration Settings and fix this."
                        )}
                    </Link>
                </Alert>
            ) : !hasTss ? (
                <Alert type="warning">
                    {t(
                        "backoffice.integration.fiskaly.tss_not_found",
                        "The Fiskaly Integration does not have a TSS registered."
                    )}
                    <Link
                        style={styles.alertLink}
                        to={{
                            screen: "INTEGRATION_EDIT",
                            params: { id: fiskalyIntegration.id },
                        }}
                    >
                        {t(
                            "backoffice.integration.fiskaly.go_to_settings",
                            "Go to the Integration Settings and fix this."
                        )}
                    </Link>
                </Alert>
            ) : fiskalyClient.loading ? (
                <Loading />
            ) : (
                <Button
                    onPress={async () => {
                        await save();
                    }}
                >
                    {t(
                        "backoffice.integration.fiskaly.sync_cash_register",
                        "Sync cash reg. w/ Fiskaly"
                    )}
                </Button>
            )}
        </Surface>
    );
}

const styleFunc: StyleFunction = theme => ({
    asideContent: {
        width: "auto",

        paddingHorizontal: theme.spacingScale * 2,
        paddingVertical: theme.spacingScale,
        rowGap: theme.spacingScale,
    },

    link: {
        ...theme.styles.link,
    },

    alertLink: {
        ...theme.styles.link,
        color: theme.colors.white,
    },
});
